/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { MultiSelect } from '@eliadmin/core/src/components/ui/multi-select';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { Popover, PopoverTrigger, PopoverContent } from '@eliadmin/core/src/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CalendarIcon, X } from 'lucide-react';
import { Calendar, localeMapping } from '@eliadmin/core/src/components/ui/calendar';
import { useId, useMemo, useState } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { FiEyeOff, FiEye } from 'react-icons/fi';
import type { InputProps } from 'react-select';
import { format as dateFnsFormat, isValid } from 'date-fns';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { enUS } from 'date-fns/locale';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';

type FieldProps<TFormValues extends FieldValues> = {
  control: Control<TFormValues, any, any>;
  name: FieldPath<TFormValues>;
  placeholder?: string;
  label?: string | React.ReactNode;
  required?: boolean;
  type?: InputProps['type'] | 'textarea';
  options?: { value: string | number; label: string }[];
  description?: string | React.ReactNode;
  isDisabled?: boolean;
  className?: {
    wrapper?: string;
    radioGroup?: string;
    radioGroupItem?: string;
  };
  menuPlacement?: 'top' | 'bottom';
  maxSelection?: number;
};

// react-select uses inputId for its focusable input; id names its container.
const MultiSelectControl = ({ id, ...props }: React.ComponentProps<typeof MultiSelect>) => (
  <MultiSelect {...props} inputId={id} />
);

export function Field<TFormValues extends FieldValues>({
  className,
  ...props
}: FieldProps<TFormValues>) {
  const { t } = useTranslations();
  const fieldId = useId();
  const [open, setOpen] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const handlePasswordMouseDown = () => setShowPassword(true);
  const handlePasswordMouseUp = () => setShowPassword(false);
  const localeCode = useLocaleStore(s => s.currentLocale);
  const locale = localeMapping[localeCode] || enUS;

  const content = useMemo(() => {
    if (props.type === 'multiselect') {
      if (!props.options) {
        throw new Error('MultiSelect type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            const handleChange = (newValue: any) => {
              // If maxSelection is set, limit the number of selections
              if (props.maxSelection && newValue && newValue.length > props.maxSelection) {
                // Remove the last selected item and keep the new selection
                const limitedValue = newValue.slice(-props.maxSelection);
                field.onChange(limitedValue);
              } else {
                field.onChange(newValue);
              }
            };

            return (
              <FormItem required={props.required} className="relative min-h-[56px]">
                {props.label && (
                  <FormLabel className="pb-2 pl-3 font-bold">{props.label}</FormLabel>
                )}
                <FormControl>
                  <MultiSelectControl
                    options={props.options} // Options passed in
                    isMulti={true} // Multi select enabled
                    placeholder={props.placeholder} // Placeholder text
                    value={field.value ?? []} // Controlled field value
                    onChange={handleChange} // Handling changes with max selection limit
                    isDisabled={props.isDisabled} // Disabled state
                    noOptionsMessage={() => 'No options available'} // Custom message for no options
                    stylesConfig={{}} // Custom styles if needed (optional)
                    menuPlacement={props.menuPlacement || 'bottom'}
                    classNamesConfig={{
                      valueContainer(props) {
                        return cn('pl-4  bg-white');
                      },

                      control(props) {
                        return cn('!min-h-[60px] bg-white');
                      },
                      menu(props) {
                        return cn('!z-[100] !bg-white');
                      },
                      menuList(props) {
                        return cn('!z-[100] !bg-white block');
                      },
                    }} // Custom classNames if needed (optional)
                    variant={'outlined'}
                  />
                </FormControl>
                {props.description && (
                  <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                    {props.description}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="space-y-1 p-[1px]">
                {props.label && (
                  <FormLabel className="pb-2 pl-3 font-bold">{props.label}</FormLabel>
                )}
                  <Select
                    value={field.value == null ? '' : String(field.value)}
                    onValueChange={value => {
                      // Radix's hidden native select emits an empty change during
                      // form reset. There is no empty option in this control.
                      if (value !== '') field.onChange(value);
                    }}
                    disabled={props.isDisabled}
                  >
                    <FormControl>
                    <SelectTrigger
                      className="rounded-full border border-border bg-white pl-2 text-black"
                      placeholder={props.placeholder}
                    >
                      <SelectValue placeholder={props.placeholder} />
                    </SelectTrigger>
                    </FormControl>
                    <SelectContent className="max-h-[70vh] overflow-y-auto sm:max-h-[50vh]">
                      {props.options?.map(o => (
                        <SelectItem key={o.value} value={String(o.value)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                {props.description && (
                  <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                    {props.description}
                  </p>
                )}
                <div className="pl-5">
                  <FormMessage />
                </div>
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'radio') {
      if (!props.options) {
        throw new Error('Radio type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem
                required={props.required}
                className={cn('flex flex-col gap-2 p-[1px]', className?.wrapper)}
              >
                {props.label && <FormLabel htmlFor={undefined} id={`${fieldId}-label`} className="font-bold">{props.label}</FormLabel>}
                <FormControl>
                  <RadioGroup
                    aria-labelledby={props.label ? `${fieldId}-label` : undefined}
                    value={field.value == null ? '' : String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                    className="flex max-w-full flex-row flex-wrap gap-4 pl-3"
                  >
                    {props.options?.map(o => (
                      <div key={o.value} className="flex gap-2">
                        <RadioGroupItem
                          id={`${fieldId}-radio-${o.value}`}
                          value={String(o.value)}
                          className={className?.radioGroupItem}
                        />
                        <Label
                          className="flex items-center whitespace-nowrap"
                          htmlFor={`${fieldId}-radio-${o.value}`}
                        >
                          <p className="text-sm font-semibold text-foreground">{o.label}</p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                {props.description && (
                  <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                    {props.description}
                  </p>
                )}
                <div className="pl-5">
                  <FormMessage />
                </div>
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'checkbox') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
                <div className="flex w-full flex-row items-center pl-4">
                  <FormControl>
                  <Checkbox
                    {...(field as any)}
                    checked={!!field.value}
                    onCheckedChange={field.onChange}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                  />
                  </FormControl>
                  {props.label && <FormLabel>{props.label}</FormLabel>}
                </div>
              <div className="pl-5">
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'date') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field, fieldState }) => {
            const isError = fieldState.error;
            const displayDate = field.value ? new Date(field.value as any) : null;

            return (
              <FormItem className="min-h-[56px]">
                <div className="relative min-h-[56px]">
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                        <Button
                          aria-label={typeof props.label === 'string' ? props.label : undefined}
                          variant="outline"
                          type="button"
                          className={cn(
                            'relative h-[56px] w-full justify-start rounded-full border border-border p-3 pl-10 pr-10 text-left text-base font-normal hover:bg-primary/40',
                            !field.value && 'text-muted-foreground',
                            isError && 'border-destructive text-destructive',
                          )}
                          disabled={props.isDisabled}
                        >
                          <CalendarIcon className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                          {displayDate && !isNaN(displayDate.getTime())
                            ? dateFnsFormat(displayDate as Date, 'PPP', { locale })
                            : props.placeholder || 'Pick a date'}
                        </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent side="bottom" align="start" className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={displayDate ?? undefined}
                          defaultMonth={displayDate ?? undefined}
                          onSelect={date => {
                            field.onChange(date as any);
                            setOpen(false); // Close the popover when a date is selected
                          }}
                          disabled={props.isDisabled}
                          autoFocus
                          startMonth={new Date(1920, 0)}
                          endMonth={new Date(new Date().getFullYear() + 5, 11)}
                        />
                      </PopoverContent>
                    </Popover>
                  {field.value && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 hover:bg-primary/20"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        field.onChange(null as any);
                        setOpen(false);
                      }}
                      aria-label="Clear date"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {props.description && (
                  <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                    {props.description}
                  </p>
                )}
                <div className="h-5 pl-5">
                  <FormMessage />
                </div>
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'checkbox-group') {
      if (!props.options) {
        throw new Error('CheckboxGroup type requires options prop');
      }

      return (
        <div className="flex flex-col gap-4">
          <FormField
            control={props.control}
            name={props.name}
            render={({ field }) => (
              <FormItem required={props.required} className="flex flex-col gap-4">
                <FormLabel htmlFor={undefined} id={`${fieldId}-label`} className="text-sm md:text-base">{props.label}</FormLabel>
                <FormControl>
                <div role="group" aria-labelledby={`${fieldId}-label`} className={cn('ml-4 flex flex-col gap-4 md:flex-row', className?.wrapper)}>
                  {props.options?.map?.(o => (
                    <div key={o.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`${fieldId}-checkbox-${o.value}`}
                        value={o.value}
                        checked={field.value?.includes?.(o.value) ?? false}
                        onCheckedChange={checked => {
                          if (checked) {
                            field.onChange([...(field.value || []), o.value]);
                          } else {
                            field.onChange((field.value || []).filter(v => v !== o.value));
                          }
                        }}
                        disabled={props.isDisabled}
                      />
                      <Label className="cursor-text select-none" htmlFor={`${fieldId}-checkbox-${o.value}`}>
                        {o.label}
                      </Label>
                    </div>
                  ))}
                </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      );
    }

    if (props.type === 'tel') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required} className="space-y-1 p-[1px]">
              {props.label && (
                <FormLabel className={cn('pb-2 pl-3 font-bold')}>{props.label}</FormLabel>
              )}
              <FormControl>
                <PhoneInputField
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  value={field.value ?? ''}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  className="rounded-full"
                  inputStyle={{
                    backgroundColor: 'hsl(var(--input)) !important',
                    height: '48px !important',
                    paddingLeft: '20px',
                    width: '100%',
                    borderEndEndRadius: '24px',
                    borderStartEndRadius: '24px',
                    border: 'none',
                  }}
                  countrySelectorStyleProps={{
                    buttonClassName: 'bg-input',
                    buttonStyle: {
                      height: '48px !important',
                      backgroundColor: 'hsl(var(--input)) !important',
                      borderStartStartRadius: '24px',
                      borderEndStartRadius: '24px',
                      paddingLeft: '12px',
                      border: 'none',
                    },
                  }}
                />
              </FormControl>
              {props.description && (
                <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                  {props.description}
                </p>
              )}
              <div className="pl-5">
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'textarea') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem
              required={props.required}
              className={cn(
                'min-h-[56px]',
                props.label && 'flex flex-col items-start justify-start gap-3',
                className?.wrapper,
              )}
            >
              {props.label && (
                <FormLabel className="text-sm font-normal text-foreground md:text-base">
                  {props.label}
                </FormLabel>
              )}
              <FormControl className="min-h-[56px]">
                <Textarea
                  placeholder={props.placeholder}
                  {...(field as any)}
                  value={field.value ?? ''}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  rows={8}
                  className={cn(
                    'min-h-[150px] w-full rounded-lg border border-border bg-[#f6f7fb] p-5 text-base',
                    className?.wrapper,
                  )}
                />
              </FormControl>
              {props.description && (
                <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                  {props.description}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required} className="space-y-1 p-[1px]">
            {props.label && (
              <FormLabel className={cn('pb-2 pl-3 font-bold', props.isDisabled && 'opacity-50')}>
                {props.label}
              </FormLabel>
            )}
            <FormControl>
              <Input
                placeholder={props.placeholder}
                type={props.type}
                {...(showPassword && { type: 'text' })}
                {...(field as any)}
                value={field.value ?? ''}
                disabled={props.isDisabled}
                aria-disabled={props.isDisabled}
                classNames={{
                  main: cn('rounded-full px-5', props.isDisabled && 'opacity-50'),
                  input: 'rounded-full px-2',
                  wrapper: cn('rounded-full'),
                }}
                after={
                  props.type === 'password' && (
                    <Button
                      type="button"
                      aria-label="Zobraziť heslo"
                      aria-pressed={showPassword}
                      variant="ghost"
                      size="icon"
                      className="hover:bg-primary/20"
                      onMouseDown={handlePasswordMouseDown}
                      onMouseUp={handlePasswordMouseUp}
                      onMouseLeave={handlePasswordMouseUp} // In case the cursor leaves the button area while pressing
                      onTouchStart={handlePasswordMouseDown}
                      onTouchEnd={handlePasswordMouseUp}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </Button>
                  )
                }
              />
            </FormControl>
            {props.description && (
              <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                {props.description}
              </p>
            )}
            <div className="pl-5">
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
    );
  }, [
    props.control,
    props.isDisabled,
    props.required,
    props.label,
    showPassword,
    props.name,
    props.options,
    props.placeholder,
    props.type,
    open,
  ]);

  return <div className={cn(className?.wrapper)}>{content}</div>;
}
